'use client';

import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, Users, DollarSign, Check, ShieldCheck, XCircle } from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Skeleton } from '@/components/ui/skeleton';
import { useUser } from '@clerk/nextjs';
import {
  SCRAPER_SOURCE_PLATFORMS,
  SCRAPER_SERVICE_TYPES,
} from '@/components/consts/scraperOptions';
import { PlatformIcon } from '@/components/common/PlatformIcon';
import { formatCurrency } from '@/lib/utils';

interface ScraperCardProps {
  scraperId: Id<'scrapers'>;
}

const getDeliveryTimeText = (deliveryTime: string | undefined) => {
  if (!deliveryTime) return 'Varies';
  switch (deliveryTime) {
    case '24h':
      return '< 24 Hours';
    case '1-3d':
      return '1-3 Days';
    case '3-7d':
      return '3-7 Days';
    case '7d+':
      return '1 Week+';
    default:
      return 'Varies';
  }
};

export default function ScraperCard({ scraperId }: ScraperCardProps) {
  const { user } = useUser();
  const scraper = useQuery(api.scrapers.getById, { id: scraperId });
  const provider = useQuery(
    api.users.getPublicProfile,
    scraper ? { userId: scraper.userId } : 'skip',
  );

  const isCurrentUserSeller = !!user && !!provider && provider.userId === user.id;

  if (!scraper) {
    return <ScraperCardSkeleton />;
  }

  if (!provider && scraper) {
    return <ScraperCardSkeleton />;
  }

  const platformBadges = scraper.platforms || [];
  const purchaseCount = scraper.purchaseCount || 0;
  const hasSales = purchaseCount > 0;
  const minOrderQuantity = scraper.minOrderQuantity || 1;
  const startingPrice = (scraper.price || 0) * minOrderQuantity;

  const getServiceTypeLabel = (id: string | undefined) => {
    if (!id) return '';
    const found = SCRAPER_SERVICE_TYPES.find((s) => s.id === id);
    return found ? found.label : id;
  };
  const getPlatformLabel = (id: string) => {
    const found = SCRAPER_SOURCE_PLATFORMS.find((p) => p.id === id);
    return found ? found.label : id;
  };

  return (
    <div className="card bg-card border border-[hsl(var(--border))] rounded-lg overflow-hidden flex flex-col h-full hover:shadow-md hover:border-primary/30 transition-all duration-200 min-h-[300px]">
      <div className="p-4 sm:p-5 pb-2 sm:pb-3 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {scraper.serviceType && (
            <span className="text-xs font-medium bg-primary/20 text-primary px-2 py-0.5 rounded-md">
              {getServiceTypeLabel(scraper.serviceType)}
            </span>
          )}
          {hasSales && (
            <span className="text-xs font-medium bg-[hsl(var(--success-DEFAULT))]/20 text-[hsl(var(--success-DEFAULT))] px-2 py-0.5 rounded-md flex items-center gap-1">
              <Users className="w-3 h-3" /> {purchaseCount} sold
            </span>
          )}
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-foreground line-clamp-1 break-all">
          <Link href={`/scrapers/${scraper.slug || scraper._id}`} className="hover:underline">
            {scraper.title}
          </Link>
        </h3>
      </div>

      <div className="p-5 pt-3 flex-grow flex flex-col">
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 sm:mb-4">
          {scraper.description}
        </p>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 py-2 sm:py-3 border-y border-[hsl(var(--border))] mb-3 sm:mb-4">
          <div className="flex items-center gap-2">
            <span className="bg-primary/15 p-1 rounded-md">
              <DollarSign className="w-4 h-4 text-primary" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground">Cost per lead</p>
              <p className="text-sm sm:text-base font-semibold text-foreground">
                {formatCurrency(scraper.price)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-primary/15 p-1 rounded-md">
              <Clock className="w-4 h-4 text-primary" />
            </span>
            <div>
              <p className="text-xs text-muted-foreground">Est. Delivery</p>
              <p className="text-sm sm:text-base font-semibold text-foreground">
                {getDeliveryTimeText(scraper.deliveryTime)}
              </p>
            </div>
          </div>
        </div>

        {minOrderQuantity > 1 && (
          <div className="mb-3 sm:mb-4">
            <p className="text-xs text-muted-foreground">Minimum order: {minOrderQuantity} leads</p>
          </div>
        )}

        {platformBadges.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
            {platformBadges.map((platform) => (
              <span
                key={platform}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-md"
              >
                <PlatformIcon platformId={platform} size={14} />
                {getPlatformLabel(platform)}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto">
          {isCurrentUserSeller ? (
            <div className="w-full px-3 py-2 bg-primary/10 text-primary rounded-md text-center text-sm">
              <div className="flex items-center justify-center gap-1.5">
                <Check className="w-3.5 h-3.5" />
                <span className="font-medium">You are the seller</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-muted-foreground">
                    Verified Service
                  </span>
                </div>
              </div>
              <Button
                asChild
                variant="default"
                className="w-full h-full min-h-[40px] bg-primary hover:bg-primary/90 text-primary-foreground rounded-md font-medium text-sm shadow-sm"
              >
                <Link href={`/scrapers/${scraper.slug || scraper._id}`}>View Details</Link>
              </Button>
            </div>
          )}

          {scraper.status === 'cancelled' && (
            <div className="mt-2 p-2 bg-destructive/15 rounded-md border border-destructive/30">
              <div className="flex items-center text-destructive">
                <XCircle className="w-3.5 h-3.5 mr-1.5" />
                <span className="text-xs font-medium">This scraper has been cancelled</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScraperCardSkeleton() {
  return (
    <div className="bg-card border border-[hsl(var(--border))] rounded-lg overflow-hidden flex flex-col h-full min-h-[300px]">
      <div className="p-4 sm:p-5 pb-2 sm:pb-3 border-b border-[hsl(var(--border))]">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Skeleton className="h-5 w-20 rounded-md" />
          <Skeleton className="h-5 w-16 rounded-md" />
        </div>
        <Skeleton className="h-6 w-3/4 mb-1" />
      </div>

      <div className="p-5 pt-3 flex-grow flex flex-col">
        <Skeleton className="h-4 w-full mb-1" />
        <Skeleton className="h-4 w-5/6 mb-3 sm:mb-4" />

        <div className="grid grid-cols-2 gap-3 sm:gap-4 py-2 sm:py-3 border-y border-[hsl(var(--border))] mb-3 sm:mb-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-md p-1" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-md p-1" />
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
          <Skeleton className="h-5 w-16 rounded-md" />
          <Skeleton className="h-5 w-20 rounded-md" />
          <Skeleton className="h-5 w-12 rounded-md" />
        </div>

        <div className="mt-auto">
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>
    </div>
  );
}
